import React, { useState, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Database,
  Play,
  ArrowClockwise,
  Check,
  X,
  Clock,
  SpinnerGap,
  CaretDown,
  CaretUp,
  Lightning,
  ChartBar,
  Calendar,
  Warning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const Admin = () => {
  const { token, user } = useAuth();
  const api = createApiClient(token);

  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningSource, setRunningSource] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user?.is_admin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sourcesRes, jobsRes, healthRes] = await Promise.all([
        api.get('/admin/scrape/sources'),
        api.get('/admin/scrape/jobs?limit=50'),
        api.get('/admin/scrape/health')
      ]);
      setSources(sourcesRes.data.sources || []);
      setJobs(jobsRes.data.jobs || []);
      setHealth(healthRes.data.scrapers || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const runScraper = async (sourceName) => {
    setRunningSource(sourceName);
    try {
      const response = await api.post(`/admin/scrape/run?source_name=${sourceName}`);
      toast.success(`Scraper ${sourceName} completed: ${response.data.items_extracted} items extracted`);
      fetchData();
    } catch (error) {
      console.error('Error running scraper:', error);
      toast.error(`Failed to run ${sourceName} scraper`);
    } finally {
      setRunningSource(null);
    }
  };

  const runAllScrapers = async () => {
    setRunningAll(true);
    try {
      const response = await api.post('/admin/scrape/run');
      const results = response.data.results || [];
      const successful = results.filter(r => r.status === 'success').length;
      toast.success(`Completed ${successful}/${results.length} scrapers`);
      fetchData();
    } catch (error) {
      console.error('Error running all scrapers:', error);
      toast.error('Failed to run scrapers');
    } finally {
      setRunningAll(false);
    }
  };

  const getSourceStats = (sourceName) => {
    const sourceJobs = jobs.filter(j => j.source_name === sourceName);
    if (sourceJobs.length === 0) return null;
    
    const lastJob = sourceJobs[0];
    const successCount = sourceJobs.filter(j => j.status === 'success').length;
    
    return {
      lastJob,
      successRate: Math.round((successCount / sourceJobs.length) * 100),
      totalRuns: sourceJobs.length
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceTypeColor = (type) => {
    switch (type) {
      case 'canonical': return 'bg-purple-100 text-purple-800';
      case 'discovery': return 'bg-blue-100 text-blue-800';
      case 'specials': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
        <SpinnerGap className="w-12 h-12 animate-spin text-[#A94438]" />
      </div>
    );
  }

  if (!user?.is_admin) {
    return (
      <div className="container-app py-8 pb-8">
        <div className="empty-state">
          <Database weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">Admin access required</h3>
          <p className="empty-state-text">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-8 pb-8" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918]">
            Scraper Admin
          </h1>
          <p className="text-[#7A7873] mt-1">
            Manage restaurant discovery scrapers
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={fetchData}
            variant="outline"
            className="rounded-full"
            data-testid="refresh-btn"
          >
            <ArrowClockwise weight="bold" className="w-5 h-5 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={runAllScrapers}
            disabled={runningAll}
            className="btn-primary"
            data-testid="run-all-btn"
          >
            {runningAll ? (
              <SpinnerGap className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Lightning weight="fill" className="w-5 h-5 mr-2" />
            )}
            Run All Scrapers
          </Button>
        </div>
      </div>

      {/* Scraper Health Dashboard */}
      {health.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="scraper-health">
          {health.map((s) => (
            <div
              key={s.source_name}
              className={`card-elevated p-4 border-l-4 ${
                s.last_status === 'success' ? 'border-l-green-500' :
                s.last_status === 'failed' ? 'border-l-red-500' : 'border-l-gray-300'
              }`}
              data-testid={`health-${s.source_name}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-[#1A1918]">{s.source_name}</h3>
                <Badge className={
                  s.last_status === 'success' ? 'bg-green-100 text-green-800' :
                  s.last_status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-600'
                }>
                  {s.last_status === 'never_run' ? 'Never Run' : s.last_status}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="text-[#7A7873]">Restaurants</div>
                <div className="font-medium text-right">{s.active_restaurants}</div>
                <div className="text-[#7A7873]">Success Rate</div>
                <div className="font-medium text-right">{s.success_rate !== null ? `${s.success_rate}%` : '—'}</div>
                <div className="text-[#7A7873]">Last Run</div>
                <div className="font-medium text-right">{formatDate(s.last_run)}</div>
                <div className="text-[#7A7873]">Last Extracted</div>
                <div className="font-medium text-right">{s.last_items_extracted}</div>
              </div>
              {s.last_error && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 truncate" title={s.last_error}>
                  <Warning weight="fill" className="w-3 h-3 inline mr-1" />
                  {s.last_error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Sources Panel */}
        <div className="card-elevated card-padding">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Database weight="duotone" className="w-5 h-5 text-[#A94438]" />
            Data Sources ({sources.length})
          </h2>

          <div className="space-y-3">
            {sources.map((source) => {
              const stats = getSourceStats(source.name);
              const isRunning = runningSource === source.name;

              return (
                <div
                  key={source.name}
                  className="p-4 border border-[#EAE8E3] rounded-lg hover:border-[#A94438]/30 transition-colors"
                  data-testid={`source-${source.name}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#1A1918]">
                          {source.name}
                        </h3>
                        <Badge className={getSourceTypeColor(source.type)}>
                          {source.type}
                        </Badge>
                      </div>

                      {stats ? (
                        <div className="flex items-center gap-4 text-sm text-[#7A7873]">
                          <span className="flex items-center gap-1">
                            <Clock weight="duotone" className="w-4 h-4" />
                            {formatDate(stats.lastJob.finished_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <ChartBar weight="duotone" className="w-4 h-4" />
                            {stats.successRate}% success
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-[#7A7873]">Never run</p>
                      )}

                      {stats?.lastJob && (
                        <div className="flex gap-3 mt-2 text-xs">
                          <span className="text-green-600">
                            {stats.lastJob.items_extracted || 0} extracted
                          </span>
                          <span className="text-blue-600">
                            {stats.lastJob.items_matched || 0} matched
                          </span>
                          {stats.lastJob.items_failed > 0 && (
                            <span className="text-red-600">
                              {stats.lastJob.items_failed} failed
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => runScraper(source.name)}
                      disabled={isRunning || runningAll}
                      size="sm"
                      className="rounded-full"
                      data-testid={`run-${source.name}`}
                    >
                      {isRunning ? (
                        <SpinnerGap className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play weight="fill" className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Jobs Panel */}
        <div className="card-elevated card-padding">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar weight="duotone" className="w-5 h-5 text-[#4B5945]" />
            Recent Jobs
          </h2>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {jobs.length === 0 ? (
              <div className="text-center py-8 text-[#7A7873]">
                <Database weight="duotone" className="w-10 h-10 mx-auto mb-2 text-[#E8DCD0]" />
                <p>No scrape jobs yet</p>
              </div>
            ) : (
              jobs.map((job, index) => (
                <Collapsible
                  key={job.id || index}
                  open={expandedJob === job.id}
                  onOpenChange={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <CollapsibleTrigger asChild>
                    <div
                      className="flex items-center justify-between p-3 border border-[#EAE8E3] rounded-lg cursor-pointer hover:bg-[#F9F8F5] transition-colors"
                      data-testid={`job-${job.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {job.status === 'success' ? (
                          <Check weight="bold" className="w-5 h-5 text-green-600" />
                        ) : job.status === 'running' ? (
                          <SpinnerGap className="w-5 h-5 animate-spin text-blue-600" />
                        ) : (
                          <X weight="bold" className="w-5 h-5 text-red-600" />
                        )}
                        <div>
                          <span className="font-medium text-[#1A1918]">
                            {job.source_name}
                          </span>
                          <span className="text-sm text-[#7A7873] ml-2">
                            {formatDate(job.started_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(job.status)}>
                          {job.status}
                        </Badge>
                        {expandedJob === job.id ? (
                          <CaretUp weight="bold" className="w-4 h-4 text-[#7A7873]" />
                        ) : (
                          <CaretDown weight="bold" className="w-4 h-4 text-[#7A7873]" />
                        )}
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 p-4 bg-[#F9F8F5] rounded-lg text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[#7A7873]">Items Seen</p>
                          <p className="font-semibold">{job.items_seen || 0}</p>
                        </div>
                        <div>
                          <p className="text-[#7A7873]">Items Extracted</p>
                          <p className="font-semibold">{job.items_extracted || 0}</p>
                        </div>
                        <div>
                          <p className="text-[#7A7873]">Items Matched</p>
                          <p className="font-semibold">{job.items_matched || 0}</p>
                        </div>
                        <div>
                          <p className="text-[#7A7873]">Items Failed</p>
                          <p className="font-semibold text-red-600">{job.items_failed || 0}</p>
                        </div>
                      </div>
                      {job.error_message && (
                        <div className="mt-3 p-2 bg-red-50 rounded border border-red-200">
                          <div className="flex items-center gap-2 text-red-800">
                            <Warning weight="fill" className="w-4 h-4" />
                            <span className="font-medium">Error</span>
                          </div>
                          <p className="text-red-700 text-xs mt-1">{job.error_message}</p>
                        </div>
                      )}
                      <div className="mt-3 text-xs text-[#7A7873]">
                        <p>Started: {formatDate(job.started_at)}</p>
                        <p>Finished: {formatDate(job.finished_at)}</p>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-elevated p-4 text-center">
          <div className="text-2xl font-bold text-[#A94438]">
            {sources.length}
          </div>
          <p className="text-sm text-[#7A7873]">Data Sources</p>
        </div>
        <div className="card-elevated p-4 text-center">
          <div className="text-2xl font-bold text-[#4B5945]">
            {jobs.filter(j => j.status === 'success').length}
          </div>
          <p className="text-sm text-[#7A7873]">Successful Jobs</p>
        </div>
        <div className="card-elevated p-4 text-center">
          <div className="text-2xl font-bold text-red-600">
            {jobs.filter(j => j.status === 'failed').length}
          </div>
          <p className="text-sm text-[#7A7873]">Failed Jobs</p>
        </div>
        <div className="card-elevated p-4 text-center">
          <div className="text-2xl font-bold text-[#1A1918]">
            {jobs.reduce((sum, j) => sum + (j.items_extracted || 0), 0)}
          </div>
          <p className="text-sm text-[#7A7873]">Total Items Extracted</p>
        </div>
      </div>
    </div>
  );
};

export default Admin;
